import React from 'react';
import {
  AbsoluteFill,
  useVideoConfig,
  useCurrentFrame,
  interpolate,
  spring,
} from 'remotion';

/**
 * 历史时间线模板
 * 
 * 功能：展示历史事件的时间线
 * 应用场景：历史教学、科技发展历程、人物传记
 * 
 * 参数：
 * - events: 历史事件数组
 * - colors: 配色方案
 * - durationPerEvent: 每个事件的持续时间（秒）
 * - layout: 布局方式（'vertical' | 'horizontal'）
 */

interface HistoricalEvent {
  year: string;
  title: string;
  description: string;
  icon?: string; // emoji 或图标
}

interface Props {
  events: HistoricalEvent[];
  colors?: {
    primary: string;
    secondary: string;
    accent: string;
    background: string;
    text: string;
    timeline: string;
  };
  durationPerEvent?: number;
  layout?: 'vertical' | 'horizontal';
}

export const HistoricalTimelineTemplate: React.FC<Props> = ({
  events,
  colors = {
    primary: '#3B82F6',
    secondary: '#10B981',
    accent: '#F59E0B',
    background: '#0F172A',
    text: '#F9FAFB',
    timeline: '#475569',
  },
  durationPerEvent = 3,
  layout = 'vertical',
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  
  // 每个事件的持续时间（帧数）
  const eventDuration = durationPerEvent * fps;
  
  return (
    <AbsoluteFill
      style={{
        backgroundColor: colors.background,
        padding: 60,
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* 标题 */}
      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 60,
          right: 60,
          fontSize: 48,
          fontWeight: 'bold',
          color: colors.text,
          opacity: interpolate(frame, [0, 30], [0, 1], {
            extrapolateRight: 'clamp',
          }),
          transform: `translateY(${interpolate(frame, [0, 30], [-20, 0], {
            extrapolateRight: 'clamp',
          })}px)`,
        }}
      >
        📜 历史时间线
      </div>
      
      {/* 时间线容器 */}
      <div
        style={{
          position: 'absolute',
          top: 120,
          left: 60,
          right: 60,
          bottom: 40,
          display: 'flex',
          flexDirection: layout === 'vertical' ? 'column' : 'row',
          alignItems: layout === 'vertical' ? 'flex-start' : 'center',
          gap: 40,
        }}
      >
        {/* 主时间轴线 */}
        <div
          style={{
            position: 'absolute',
            left: layout === 'vertical' ? 80 : 40,
            top: layout === 'vertical' ? 0 : '50%',
            width: layout === 'vertical' ? 4 : '100%',
            height: layout === 'vertical' ? '100%' : 4,
            backgroundColor: colors.timeline,
            opacity: interpolate(frame, [0, 60], [0, 1], {
              extrapolateRight: 'clamp',
            }),
          }}
        />
        
        {/* 事件列表 */}
        {events.map((event, index) => {
          const eventStartFrame = index * eventDuration;
          const localFrame = frame - eventStartFrame;
          
          // 节点出现动画
          const nodeOpacity = interpolate(localFrame, [0, 20], [0, 1], {
            extrapolateRight: 'clamp',
          });
          
          const nodeScale = spring({
            frame: localFrame,
            fps,
            config: {
              damping: 15,
              stiffness: 200,
              mass: 0.5,
            },
          });
          
          // 内容滑入动画
          const contentTranslate = interpolate(
            localFrame,
            [10, 40],
            [layout === 'vertical' ? -50 : -30, 0],
            { extrapolateRight: 'clamp' }
          );
          
          const contentOpacity = interpolate(localFrame, [10, 40], [0, 1], {
            extrapolateRight: 'clamp',
          });
          
          // 计算位置
          const topOffset = layout === 'vertical' 
            ? index * (events.length > 4 ? 180 : events.length > 3 ? 160 : 150) 
            : '50%';
          const leftOffset = layout === 'vertical'
            ? 80
            : 40 + (index * ((1920 - 120) / events.length));
          
          return (
            <div
              key={index}
              style={{
                position: 'absolute',
                top: topOffset,
                left: leftOffset,
                display: 'flex',
                alignItems: 'center',
                gap: 24,
                transform: layout === 'horizontal'
                  ? `translateY(-50%) translateX(${contentTranslate}px)`
                  : `translateX(${contentTranslate}px)`,
                opacity: Math.min(nodeOpacity, contentOpacity),
              }}
            >
              {/* 时间节点 */}
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  backgroundColor: colors.primary,
                  border: `4px solid ${colors.background}`,
                  boxShadow: `0 0 0 4px ${colors.timeline}`,
                  transform: `scale(${nodeScale})`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 12,
                  zIndex: 10,
                }}
              >
                {event.icon || '•'}
              </div>
              
              {/* 事件卡片 */}
              <div
                style={{
                  backgroundColor: colors.primary + '15',
                  border: `2px solid ${colors.primary}`,
                  borderRadius: 12,
                  padding: '16px 24px',
                  maxWidth: layout === 'vertical' ? 900 : 300,
                  backdropFilter: 'blur(10px)',
                }}
              >
                {/* 年份 */}
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 'bold',
                    color: colors.accent,
                    marginBottom: 8,
                  }}
                >
                  {event.year}
                </div>
                
                {/* 标题 */}
                <div
                  style={{
                    fontSize: 24,
                    fontWeight: '600',
                    color: colors.text,
                    marginBottom: 8,
                  }}
                >
                  {event.title}
                </div>
                
                {/* 描述 */}
                <div
                  style={{
                    fontSize: 22,
                    color: colors.text + 'CC',
                    lineHeight: 1.5,
                  }}
                >
                  {event.description}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* 进度指示器 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 60,
          right: 60,
          height: 4,
          backgroundColor: colors.timeline + '40',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${(frame / (events.length * eventDuration)) * 100}%`,
            height: '100%',
            backgroundColor: colors.accent,
            borderRadius: 2,
          }}
        />
      </div>
    </AbsoluteFill>
  );
};
